import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  Fragment
} from 'react';
import PropTypes from 'prop-types';
import _ceil from 'lodash/ceil';
import _round from 'lodash/round';
import cln from 'classnames';

import { useFirstRender, useWindowSize } from 'hooks';
import { calculatePages } from 'util/calculations';
import styles from './styles.module.scss';

const getNewPages = (newPage, newMobilePage) => ({
  page: newPage,
  mobilePage: newMobilePage
});

const isLargePage = (currentPage) => currentPage.toString().length >= 4;

const renderTextPage = (currentPage) =>
  isLargePage(currentPage)
    ? `${currentPage.toString().substring(0, 4)}...`
    : currentPage;

export default function PaginationList({
  list,
  onChange,
  mobileListener = true,
  totalLimit
}) {
  if (!list || Array.isArray(list) || !list.params || !list.totalPages)
    return null;

  const [pages, setPages] = useState([]);

  const isFirstRender = useFirstRender();
  const { isMobile } = useWindowSize({ largeMobile: true })[1];

  const { totalPages, totalElements } = list;
  const { page = 1, size = 12, mobilePage, mobileSize } = list.params;

  const elementsByLimit = useMemo(
    () =>
      totalLimit && totalElements >= totalLimit ? totalLimit : totalElements,
    [totalLimit, totalElements]
  );

  const desktopTotalPages = useMemo(
    () => (totalLimit ? _round(_ceil(elementsByLimit / size)) : totalPages),
    [elementsByLimit, size, totalLimit, totalPages]
  );

  const mobileTotalPages = useMemo(
    () => _round(_ceil(elementsByLimit / mobileSize)),
    [elementsByLimit, mobileSize]
  ); // TODO: add mobile total pages to reducers lists

  const totalPagesByScreen = useMemo(
    () => (isMobile ? mobileTotalPages : desktopTotalPages),
    [isMobile, mobileTotalPages, desktopTotalPages]
  );

  const pagination = useMemo(
    () => calculatePages(page, totalPagesByScreen, 8),
    [page, totalPagesByScreen]
  );

  const handleClick = useCallback(
    (index) => {
      onChange(getNewPages(index, mobilePage));
    },
    [onChange, mobilePage]
  );

  const handleLoadMore = () => {
    const nextPage = mobilePage + 1;
    const newMobilePage = nextPage <= mobileTotalPages ? nextPage : mobilePage;
    onChange(getNewPages(page, newMobilePage));
  };

  useEffect(() => {
    if (!isFirstRender && mobileListener) {
      onChange(getNewPages(page, mobilePage));
    }
  }, [isMobile, mobileListener]);

  useEffect(() => {
    if (pagination) {
      const { startPage, endPage } = pagination;

      setPages([]);

      let newPagesToRender = [];

      for (let i = startPage; i <= (endPage || 1); i += 1) {
        const newPages = Array.from({ length: i + 1 }, (_, index) => index);
        newPagesToRender = newPages.slice(startPage, newPages.length);
      }

      setPages(newPagesToRender);
    }
  }, [pagination, totalPagesByScreen]);

  if (pagination) {
    const { startPage, endPage, first, last } = pagination;

    const isFirst = page === first;
    const isLast = page >= last;

    return (
      <>
        { !isLast ?
          <div className={styles.mobilePagination}>
            <button type="button" onClick={handleLoadMore}>
              Cargar más
            </button>
          </div>
          : null
        }

        <div className={styles.pagination}>
          <div className="d-flex align-items-center">
            <button
              type="button"
              onClick={() => !isFirst && handleClick(page - 1)}
              className={cln(styles.prev)}
            >
              Anterior
            </button>
            
            <button
              type="button"
              onClick={() => !isFirst && handleClick(first)}
              className={cln(styles.page, isFirst && styles.active)}
            >
              {first}
            </button>
          </div>

          <div className="d-flex align-items-center">
            {page > 4 && (
              <span
                className={cln(styles.ellipsis, 'mx-1')}
                role="button"
                tabIndex="0"
                onClick={() => handleClick((startPage < 0? last : startPage ) - 1 )}
                onKeyDown={() => handleClick((startPage < 0? last : startPage ) - 1 )}
              >
                &hellip;
              </span>
            )}
          </div>

          <div className={styles.middleItems}>
            {pages.map((currentPage) => (
              <Fragment key={currentPage}>
                {currentPage !== first && currentPage !== last && (
                  <button
                    type="button"
                    onClick={() => handleClick(currentPage)}
                    className={cln(
                      styles.page,
                      page === currentPage && styles.active,
                      isLargePage(currentPage) && styles.pageLarge
                    )}
                  >
                    {renderTextPage(currentPage)}
                  </button>
                )}
              </Fragment>
            ))}
          </div>

          {desktopTotalPages > 5 && endPage !== desktopTotalPages && (
            <div className="d-flex align-items-center">
              <span
                className={cln(styles.ellipsis, 'mx-1')}
                role="button"
                tabIndex="0"
                onClick={() => handleClick(endPage + 1)}
                onKeyDown={() => handleClick(endPage + 1)}
              >
                &hellip;
              </span>
            </div>
          )}

          <div className="d-flex align-items-center">
            {startPage !== 1 && (
              <button
                type="button"
                onClick={() => !isLast && handleClick(last)}
                className={cln(
                  styles.page,
                  isLast && styles.active,
                  isLargePage(last) && styles.pageLarge
                )}
              >
                {renderTextPage(last)}
              </button>
            )}

            <button
              type="button"
              onClick={() => !isLast && handleClick(page + 1)}
              className={styles.next}
            >
              Siguiente
            </button>
          </div>
        </div>
      </>
    );
  }

  return null;
}

PropTypes.propTypes = {
  list: PropTypes.shape({
    totalPages: PropTypes.number.isRequired,
    totalElements: PropTypes.number.isRequired,
    params: PropTypes.shape({
      page: PropTypes.number.isRequired,
      mobilePage: PropTypes.number.isRequired,
      mobileSize: PropTypes.number.isRequired
    }).isRequired
  }).isRequired,
  onChange: PropTypes.func,
  mobileListener: PropTypes.bool,
  totalLimit: PropTypes.number
};
